import { FC } from 'react';
import { ICaremapNote, ICaremapNoteResponse } from '../model/NotesClient';
import { IRecording } from '../../appointments/recording/RecordingState';
import { IonItem, IonLabel, IonChip } from '@ionic/react';
import { CareMapNotesListTitle } from './CareMapNotesListTitle';
import { CareMapNotesListContent } from './CareMapNotesListContent';
import { AttachmentList } from '../generic-notes/attachments/AttachmentList';
import { RecordingInfo } from '../../appointments/recording/RecordingInfo';
import { NotesListActionItemCount } from './NotesListActionItemCount';
import { NotesListTaskCount } from './NotesListTaskCount';
import { NotesListSummarizedByAI } from './NotesListSummarizedByAI';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { TasksState } from '../../tasks/TasksState';

const useStyles = createUseStyles({
    recordingInfoWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,

        // ensure no wrapping on smaller phones
        // TODO: avoid "items" getting clipped
        marginRight: -35,

        // NOTE: don't make this a common practice, of media queries in between breakpoints. very specific case (School Team Member Detail)
        '@media (max-width: 389px)': {
            gap: 7
        }
    },

    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 4,
        marginBottom: 4
    }
});

// Same color logic as TagsField
const getTagColor = (tag: string) => {
    const tagColorOptions = [
        '#4A8FA8', // Teal (primary theme equivalent)
        '#8E5FC7', // Purple (tertiary theme equivalent)
        '#D45A2A', // Orange (secondary theme equivalent)
        '#4A8F6B', // Green (success theme equivalent)
        '#5A72C7', // Blue
        '#C768A3', // Pink
        '#3E4A5C', // Dark blue-gray
        '#D47A47', // Orange-brown
        '#C73E3E'  // Red
    ];

    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
        const char = tag.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    const colorIndex = Math.abs(hash) % tagColorOptions.length;
    const color = tagColorOptions[colorIndex];

    return {
        backgroundColor: `${color}1A`, // 10% opacity
        color: color,
        border: `1px solid ${color}`
    };
};

export const CareMapNotesListItemTags: FC<{ tags?: string[] }> = ({ tags }) => {
    const classes = useStyles();

    if (!tags || tags.length === 0) {
        return null;
    }

    const maxTagsToShow = 3;
    const visibleTags = tags.slice(0, maxTagsToShow);
    const remainingCount = tags.length - maxTagsToShow;

    return (
        <div className={classes.tagsContainer}>
            {visibleTags.map(tag => (
                <IonChip key={tag} style={getTagColor(tag)}>
                    <IonLabel style={{ fontSize: '12px' }}>{tag}</IonLabel>
                </IonChip>
            ))}
            {remainingCount > 0 && (
                <IonChip style={{ backgroundColor: 'var(--ion-color-step-100)', color: 'var(--ion-color-step-600)' }}>
                    <IonLabel style={{ fontSize: '12px' }}>+{remainingCount} more</IonLabel>
                </IonChip>
            )}
        </div>
    );
};

export const CareMapNotesListItemRecording: FC<{ recording: IRecording; taskCount?: number }> = ({ recording, taskCount }) => {
    const classes = useStyles();
    return (
        <div className={classes.recordingInfoWrapper}>
            <RecordingInfo recording={recording} />
            <NotesListSummarizedByAI recording={recording} />
            {taskCount && taskCount > 0 ? (
                <NotesListTaskCount taskCount={taskCount} />
            ) : (
                <NotesListActionItemCount recording={recording} />
            )}
        </div>
    );
};

export type ICareMapNotesListItemProps = {
    note: ICaremapNoteResponse | ICaremapNote;
    onClick?: () => void;
    button?: boolean;
    detail?: boolean;
    showPrivacy?: boolean;
    showNoteTargetHeader?: boolean;
    lines?: 'full' | 'inset' | 'none';
    sortBy?: 'updatedAt' | 'createdAt';
    searchText?: string;
};

export const CareMapNotesListItem: FC<ICareMapNotesListItemProps> = ({
    note,
    onClick,
    button = true,
    detail = true,
    showPrivacy = true,
    showNoteTargetHeader,
    lines,
    sortBy = 'updatedAt',
    searchText = ''
}) => {
    const tasksState = useInstance(TasksState);
    useSubscription(() => tasksState);

    const recording = note.otherAttributes.recording as IRecording;

    // Calculate task count dynamically by filtering tasks linked to this note
    const allTasks = tasksState.getTasks();
    const taskCount = allTasks.filter(task => {
        // Match by noteId - this is the primary way tasks are linked to notes
        return task.source?.noteId === note.noteId;
    }).length;

    return (
        <IonItem
            button={button}
            detail={detail}
            lines={lines}
            onClick={onClick}
            data-type="note-item"
            data-node-id={note.noteId}
            style={{'--border-radius': '8px'}}
        >
            <IonLabel class="ion-text-wrap">
                <CareMapNotesListTitle
                    note={note}
                    showPrivacy={showPrivacy}
                    showNoteTargetHeader={showNoteTargetHeader}
                    sortBy={sortBy}
                    searchText={searchText}
                />

                <CareMapNotesListContent note={note} searchText={searchText} />

                <CareMapNotesListItemTags tags={note.tags} />

                {!recording && (
                    <AttachmentList
                        note={note}
                        attachments={note.attachments}
                    />
                )}

                <CareMapNotesListItemRecording recording={recording} taskCount={taskCount} />
            </IonLabel>
        </IonItem>
    );
};
